/**
 * COMPONENT: BedDerogationBlockBanner
 *
 * TICKET-DEROG-04 (blocage provisoire) : sur la fiche équipage, dit pourquoi le
 * paiement du solde BED est bloqué (membre de 29 ans ou plus au départ sans
 * « Dérogation validée », copilote déclaré dans le bulletin et pas encore
 * rattaché) et comment le débloquer. Signale aussi les dates de naissance
 * manquantes, qui empêchent tout contrôle d'âge sans rien bloquer.
 *
 * Purement présentationnel : la règle vit en base (bed_derogation_check, 00310).
 */

import { AlertTriangle, Info } from 'lucide-react';
import type { BedDerogationBlock, BedDerogationPerson } from '../../lib/bed/admin';

const ROLE_LABELS: Record<string, string> = {
  pilot: 'pilote',
  copilot: 'copilote',
  member: 'membre',
};

function fullName(person: BedDerogationPerson): string {
  return [person.first_name, person.last_name].filter(Boolean).join(' ') || 'Nom inconnu';
}

function describeBlocker(person: BedDerogationPerson): string {
  if (person.source === 'bulletin') {
    return `${fullName(person)} (copilote déclaré dans le bulletin) : 29 ans ou plus au départ, pas encore membre de l’équipage.`;
  }
  const role = ROLE_LABELS[person.role] ?? person.role;
  return `${fullName(person)} (${role}) : 29 ans ou plus au départ, « Dérogation validée » non cochée.`;
}

/**
 * Situation du dossier : `payment` (solde à payer, défaut), `nothing_due`
 * (rien à payer : c'est l'envoi du bulletin qui est bloqué), `settled` (solde
 * réglé : la dérogation reste à vérifier avant l'inscription définitive).
 */
export type BedDerogationContext = 'payment' | 'nothing_due' | 'settled';

const TITLES: Record<BedDerogationContext, string> = {
  payment: 'Paiement du solde bloqué : dérogation d’âge à valider',
  nothing_due: 'Bulletin bloqué (rien à payer) : dérogation d’âge à valider',
  settled: 'Dérogation d’âge non validée',
};

const SITUATIONS: Record<BedDerogationContext, string | null> = {
  payment: null,
  nothing_due:
    'Rien à payer : le bulletin ne peut pas être envoyé (inscription définitive) tant que la dérogation n’est pas validée.',
  settled: 'Le solde est réglé : vérifiez la dérogation avant l’inscription définitive.',
};

export function BedDerogationBlockBanner({
  block,
  context = 'payment',
  showMissing = true,
}: {
  block: BedDerogationBlock;
  context?: BedDerogationContext;
  /** Dates de naissance manquantes : seulement dans le parcours BED. */
  showMissing?: boolean;
}) {
  const missing = showMissing ? (block.birth_date_missing ?? []) : [];
  if (!block.blocked && missing.length === 0) return null;

  return (
    <div className="mb-4 flex flex-col gap-2">
      {block.blocked && (
        <div
          role="alert"
          className="flex items-start gap-2 rounded-md border border-orange-200 bg-orange-50 px-3 py-2 text-sm text-orange-900"
        >
          <AlertTriangle className="mt-0.5 size-4 shrink-0 text-orange-600" />
          <div>
            <p className="font-semibold">{TITLES[context]}</p>
            {SITUATIONS[context] && <p className="mt-0.5">{SITUATIONS[context]}</p>}
            <ul className="mt-1 list-disc pl-5">
              {block.blockers.map((person, index) => (
                <li key={person.contact_id ?? `bulletin-${index}`}>{describeBlocker(person)}</li>
              ))}
            </ul>
            <p className="mt-1">
              Pour débloquer : cochez « Dérogation validée » sur la carte du membre. Un copilote
              déclaré dans le bulletin doit d’abord être ajouté à l’équipage. Prévenez ensuite
              l’équipage par email : la suite lui sera proposée sur son lien.
            </p>
          </div>
        </div>
      )}
      {missing.length > 0 && (
        <div className="flex items-start gap-2 rounded-md border border-zinc-200 bg-zinc-50 px-3 py-2 text-sm text-zinc-700">
          <Info className="mt-0.5 size-4 shrink-0 text-zinc-500" />
          <p>
            Date de naissance manquante : {missing.map(fullName).join(', ')}. L’âge ne peut pas être
            contrôlé : le paiement n’est pas bloqué.
          </p>
        </div>
      )}
    </div>
  );
}
